import React, {useState, useEffect, useMemo} from 'react';
import Chart from 'react-apexcharts';
import {Button, CircularProgress, Typography} from '@material-ui/core';

import {KeyboardDatePicker} from '@material-ui/pickers';

import dashboardService from '../../service';

import {styles} from './styles';

import {CustomSelect} from './components/CustomSelect';

const SubCategoriesItem = () => {
  const colorMap = {
    Meditações: '#316570',
    Conexão: '#ff8795',
    Yoga: '#ffac1c',
    Fé: '#ffdc00',
    Músicas: '#38b2f7',
    Exercícios: '#4cc836',
  };

  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [screenData, setScreenData] = useState([]);
  const [activeCategory, setActiveCategory] = useState('Meditações');

  const [filters, setFilters] = useState({
    platform: null,
    currentAccountType: null,
  });

  const generateGradientColors = (baseColor, total) => {
    const hexToRgb = hex => {
      const bigint = parseInt(hex.replace('#', ''), 16);

      return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255,
      };
    };

    const rgbToHex = (r, g, b) =>
      '#' +
      [r, g, b]
        .map(x => {
          const hex = x.toString(16);

          return hex.length === 1 ? '0' + hex : hex;
        })
        .join('');

    const {r, g, b} = hexToRgb(baseColor);

    return Array.from({length: total}, (_, i) => {
      const factor = 1 - i / (total * 1.8);

      return rgbToHex(
        Math.max(0, Math.floor(r * factor)),
        Math.max(0, Math.floor(g * factor)),
        Math.max(0, Math.floor(b * factor)),
      );
    });
  };

  const GetData = async () => {
    try {
      setLoading(true);

      const response = await dashboardService.getSubCategories({
        startDate,
        endDate,

        filters: {
          platform: filters.platform || null,
          currentAccountType: filters.currentAccountType || null,
        },
      });

      setScreenData(response.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    GetData();
  }, [startDate, endDate, filters]);

  const categoriesList = useMemo(() => {
    return [...new Set(screenData.map(item => item.category))].filter(Boolean);
  }, [screenData]);

  useEffect(() => {
    if (categoriesList.length && !categoriesList.includes(activeCategory)) {
      setActiveCategory(categoriesList[0]);
    }
  }, [categoriesList]);

  const filteredData = useMemo(() => {
    return screenData
      .filter(item => item.category === activeCategory)
      .sort((a, b) => b.total - a.total);
  }, [screenData, activeCategory]);

  const chartNames = filteredData.map(item => item.subcategory);

  const chartValues = filteredData.map(item => item.total);

  const chartColors = generateGradientColors(
    colorMap[activeCategory] || '#8B5CF6',
    chartValues.length,
  );

  return (
    <div
      style={{
        margin: 15,
        marginTop: 25,
        padding: 20,
        backgroundColor: '#fff',
        borderRadius: 10,
      }}>
      <Typography component="h1" variant="h6" style={{marginBottom: 20}}>
        Subcategorias
      </Typography>

      <div style={styles.flex}>
        <CustomSelect
          title="Plataforma"
          values={[
            {value: null, label: 'Todos'},
            {value: 'android', label: 'Android'},
            {value: 'ios', label: 'IOS'},
          ]}
          value={filters.platform}
          setValue={event =>
            setFilters({
              ...filters,
              platform: event,
            })
          }
        />

        <CustomSelect
          title="Assinantes"
          values={[
            {value: null, label: 'Todos'},
            {value: 'subscriber', label: 'Sim'},
            {value: 'free', label: 'Não'},
          ]}
          value={filters.currentAccountType}
          setValue={event =>
            setFilters({
              ...filters,
              currentAccountType: event,
            })
          }
        />

        <div style={styles.flexEnd}>
          <div style={styles.datePicker}>
            <KeyboardDatePicker
              value={startDate}
              format="DD/MM/YYYY"
              maxDate={endDate}
              onChange={date => {
                const newDate = new Date(date);

                if (newDate > endDate) {
                  setEndDate(newDate);
                }

                setStartDate(newDate);
              }}
            />
          </div>

          <div style={styles.datePicker}>
            <KeyboardDatePicker
              value={endDate}
              format="DD/MM/YYYY"
              minDate={startDate}
              onChange={date => {
                const newDate = new Date(date);

                if (newDate < startDate) {
                  setStartDate(newDate);
                }

                setEndDate(newDate);
              }}
            />
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: 20,
          marginBottom: 20,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
        }}>
        {categoriesList.map(cat => (
          <Button
            key={cat}
            variant={activeCategory === cat ? 'contained' : 'outlined'}
            onClick={() => setActiveCategory(cat)}
            style={{
              borderColor: colorMap[cat],
              background:
                activeCategory === cat ? colorMap[cat] : 'transparent',

              color: activeCategory === cat ? '#fff' : colorMap[cat],
            }}>
            {cat}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex-col center-a center-b">
          <CircularProgress size={80} />
        </div>
      ) : (
        <Chart
          options={{
            chart: {
              type: 'bar',
              toolbar: {
                show: false,
              },
            },

            colors: chartColors,

            plotOptions: {
              bar: {
                distributed: true,
                borderRadius: 8,
              },
            },

            dataLabels: {
              enabled: false,
            },

            xaxis: {
              categories: chartNames,

              labels: {
                rotate: -45,
              },
            },

            tooltip: {
              y: {
                formatter: val => `${val.toLocaleString('pt-BR')} acessos`,
              },
            },
          }}
          series={[
            {
              data: chartValues,
            },
          ]}
          type="bar"
          height={420}
        />
      )}
    </div>
  );
};

export default SubCategoriesItem;
